import { Optional } from "common/utils/types";
import { IPromoCode, Incentive, Rebate } from "common/types/promotions";

import { IBasePricingGroup } from "./clinics";
import { VendorSlug } from "./vendors";
import { ItemFulfillmentOption } from "./fulfillments";
import { BasePrescription } from "./prescriptions";

import {
  Coordinate,
  FrequencyUnit,
  IAddress,
  Doctor,
  IPatient,
  SubscriptionFrequency,
  BasicCatalogImage,
  IItemAndFlavorOptionsWithProduct,
  PaymentMethod,
} from ".";

/** Matches carts CartShippingAddressSerializer */
export interface CartShippingAddress extends IAddress {
  coordinates?: Optional<Coordinate>;
}

/** matches carts constants CartCreatorType */
export enum CartCreatorKind {
  Clinic = "CLINIC",
  Client = "CLIENT",
}

/** matches carts CrossSellProductLineSerializer */
export interface CrossSellProductLine {
  id: number;
  short_description: string;
  name: string;
}

/** matches carts CrossSellProductSerializer */
export interface CrossSellProduct {
  id: number;
  product_line: CrossSellProductLine;
}

/** matches carts CrossSellItemSerializer */
export interface CrossSellItem {
  id: number;
  name: string;
  product: CrossSellProduct;
  image: Optional<BasicCatalogImage>;
}

/** matches carts CommonCartClinicSerializer */
export type ICartClinic = {
  id: number;
  client_display_name: string;
  primary_address: Optional<IAddress>;
  active_pricing_group: IBasePricingGroup;
  allow_urgent_pickups: boolean;
  urgency_fee: number;
  employee_markup_percentage: Optional<string>;
};

/** Matches carts ClientCartDetailSerializer */
export interface ClientCartDetail {
  id: number;
  total: number;
  subtotal: number;
  discount: number;
  shipping: number;
  items: ICartItem[];
  unavailable_items: ICartItem[];
  client_id: number;
  has_subscription: boolean;
  is_all_pickup: boolean;
  is_checkout_link: boolean;
  checkout_link_sent_at: Optional<string>;
  clinic: ICartClinic;
  is_closed: boolean;
  is_urgent: boolean;
  has_pickup: boolean;
  is_delivery_info_complete: boolean;
  shipping_address: Optional<CartShippingAddress>;
  pickup_address: Optional<IAddress>;
  payment_method: Optional<PaymentMethod>;
  delivery_groups: IDeliveryGroup[];
  note: string;
  promo_codes: IPromoCode[];
  warning_messages: string[];
  client_handling_fees: number;
  client_purchasing_fee: number;
  discount_breakdown: DiscountBreakdown[];
  is_shipping_address_required: boolean;
  is_autoship_confirmation_required: boolean;
  is_address_confirmation_required: boolean;
  is_shipping_free: boolean;
  patients_display: string;
  is_klarna_eligible: boolean;
  missing_required_client_fields_message: Optional<string>;
  creator_type: CartCreatorKind;
  employee_savings: number;
  should_show_employee_pricing_note: boolean;
  is_eligible_for_first_autoship_discount: boolean;
  is_eligible_for_recurring_autoship_discount: boolean;
}

/** Matches carts CommonCartItemReadSerializer */
export interface ICartItem {
  id: number;
  item: IItemAndFlavorOptionsWithProduct;
  quantity: number;
  patient: Optional<IPatient>;
  subscription: Optional<ICartItemSubscription>;
  existing_subscription_modifications: Optional<ICartItemSubscriptionModifications>;
  prescription: Optional<BasePrescription>;
  subtotal: number;
  discount: number;
  discounted_subtotal: number;
  handling_fee: number;
  client_purchasing_fee: number;
  is_subscribable: boolean;
  is_pickup: boolean;
  discount_messages: string[];
  discount_message_details: CartItemDiscountMessage[];
  custom_unit_price: Optional<number>;
  default_unit_price: number;
  subscription_unit_price: number;
  flavor_selection: Optional<number>;
  subscription_incentive_discount_percentage: string;
  available_prescriptions: ICartItemAvailablePrescription[];
  is_approval_required: boolean;
  is_available_for_pickup: boolean;
  fulfillment_options: ItemFulfillmentOption[];
  primary_doctor: Doctor;
  flavors_display: Optional<string>;
  is_available: boolean;
  prescription_warning: Optional<string>;
  item_warning: Optional<string>;
  unit_discount: number;
  frequency_options: SubscriptionFrequency[];
  recommended_frequency: SubscriptionFrequency;
  refill_too_soon_reason: Optional<RefillTooSoonReason>;
  created: string;
  created_by: number | null;
  missing_required_patient_fields_message: Optional<string>;
  invalid_patient_species_message: Optional<string>;
  invalid_patient_weight_message: Optional<string>;
  should_warn_single_unit_dispense: boolean;
}

/** Matches carts CommonCartItemSubscriptionSerializer */
export interface ICartItemSubscription {
  frequency: number;
  interval_unit: FrequencyUnit;
  frequency_display: string;
}

/** matches CartItemSubscriptionCreateUpdateSerializer */
export interface ICartItemSubscriptionUpdate {
  frequency: ICartItemSubscription["frequency"];
  interval_unit: ICartItemSubscription["interval_unit"];
}
/** Matches carts CommonCartItemAvailablePrescriptionSerializer */
export interface ICartItemAvailablePrescription {
  id: number;
  prescribed_by: Optional<Doctor>;
  refills: Optional<number>;
  expiration_date: Optional<string>;
  patient: IPatient;
  remaining_fills_display: string;
  quantity_per_fill_display: string;
  prescribed_on: string;
}

/** Matches carts constants DeliveryOptionKind */
export enum DeliveryOptionKind {
  Shipped = "SHIPPED",
  Pickup = "PICKUP",
}

/** Matches carts CommonDeliveryOptionSerializer */
export interface IShippingDeliveryOption {
  kind: DeliveryOptionKind.Shipped;
  key: number;
  cost: number;
  title: string;
  description: string;
}

/** Matches carts CommonDeliveryOptionSerializer */
export interface IPickupDeliveryOption {
  kind: DeliveryOptionKind.Pickup;
  key: null;
  cost: number;
  title: string;
  description: string;
}

/** Matches carts CommonDeliveryOptionSerializer */
export type IDeliveryOption = IShippingDeliveryOption | IPickupDeliveryOption;

/** Matches carts constants DeliveryGroupKind */
export enum DeliveryGroupKind {
  Shipped = "SHIPPED",
  PickupOnly = "PICKUP_ONLY",
  Pickup = "PICKUP",
  Unavailable = "UNAVAILABLE",
}

/** Matches carts DeliveryFulfiller.slug */
export type DeliveryFulfillerSlug = VendorSlug | "SELF";

/** Matches CommonDeliveryGroupFulfillerSerializer */
export interface IDeliveryGroupFulfiller {
  key: string;
  name: string;
  slug: DeliveryFulfillerSlug;
}

/** common fields for delivery groups, you should not directly use this interface */
export interface BaseDeliveryGroup {
  key: string;
  cart_items: ICartItem[];
  fulfiller: Optional<IDeliveryGroupFulfiller>;
  description: string;
  messages: string[];
  selected_delivery_option?: IDeliveryOption;
  kind: DeliveryGroupKind;
  delivery_options: IDeliveryOption[];
}
export interface IShippingDeliveryGroup extends BaseDeliveryGroup {
  selected_delivery_option?: IShippingDeliveryOption;
  kind: DeliveryGroupKind.Shipped;
  delivery_options: IShippingDeliveryOption[];
}
export interface IPickupDeliveryGroup extends BaseDeliveryGroup {
  selected_delivery_option?: IPickupDeliveryOption;
  kind: Exclude<DeliveryGroupKind, DeliveryGroupKind.Shipped>;
  delivery_options: IPickupDeliveryOption[];
}

/** Matches CommonDeliveryGroupSerializer */
export type IDeliveryGroup = IPickupDeliveryGroup | IShippingDeliveryGroup;

export const isShippingDeliveryOption = (
  option: IDeliveryOption,
): option is IShippingDeliveryOption => option.kind === DeliveryOptionKind.Shipped;

export const isPickupDeliveryOption = (option: IDeliveryOption): option is IPickupDeliveryOption =>
  option.kind === DeliveryOptionKind.Pickup;

export const isShippingDeliveryGroup = (dg: IDeliveryGroup): dg is IShippingDeliveryGroup =>
  dg.kind === DeliveryGroupKind.Shipped;

export const isPickupDeliveryGroup = (dg: IDeliveryGroup): dg is IPickupDeliveryGroup =>
  dg.kind === DeliveryGroupKind.Pickup;

/** Matches CommonDiscountBreakdownSerializer */
export interface DiscountBreakdown {
  display: string;
  discount: number;
  incentive?: Optional<Incentive>;
  rebate?: Optional<Rebate>;
  is_promo_code?: boolean;
}

/** Matches carts CartItemDiscountMessageSerializer */
export type CartItemDiscountMessage = {
  display_text: string;
  promo_code: string | null;
};

/** Matches shape of return value of CartController.get_existing_subscription_modifications  */
export interface ICartItemSubscriptionModifications {
  frequency?: string;
  payment_method?: string;
  quantity?: number;
  next_order_date?: string;
  // Populated instead of next_order_date for v2 (AUTOSHIP_EDB) clinics, where the next
  // order date is derived at checkout rather than surfaced here.
  expected_delivery_by?: string;
}

/**  Matches carts CommonCartTaxEstimateSerializer */
export interface ICartTax {
  total_tax: number;
}

/** Matches CommonCartApplyPromoCodeSerializer */
export type CartApplyPromoCodesMutation = {
  promo_code: string;
};

/** matches carts constants PromoCodeCartValidationErrorKind */
export enum PromoCodeCartError {
  DOES_NOT_EXIST = "DOES_NOT_EXIST",
  INACTIVE = "INACTIVE",
  ONETIME_CONSUMED = "ONETIME_CONSUMED",
  NOT_APPLICABLE = "NOT_APPLICABLE",
  ALREADY_APPLIED = "ALREADY_APPLIED",
  AUTOSHIP_PROMO_BUT_ITEMS_NON_AUTOSHIP = "AUTOSHIP_PROMO_BUT_ITEMS_NON_AUTOSHIP",
  FIRST_AUTOSHIP_ELIGIBILITY_CONSUMED = "FIRST_AUTOSHIP_ELIGIBILITY_CONSUMED",
  INVALID_ITEMS_FOR_PROMO = "INVALID_ITEMS_FOR_PROMO",
  AUTOSHIP_PROMO_WITH_LOGGED_OUT_CART = "AUTOSHIP_PROMO_WITH_LOGGED_OUT_CART",
  GLOBALLY_SINGLE_USE_CONSUMED = "GLOBALLY_SINGLE_USE_CONSUMED",
  MIN_ORDER_SUBTOTAL_NOT_MET = "MIN_ORDER_SUBTOTAL_NOT_MET",
}

/** matches carts types PromoCodeCartValidationErrorData */
export type CartApplyPromoCodeErrorData = {
  kind: PromoCodeCartError;
  amount_needed?: number;
};

/** Matches expected response for ClinicCartApplyPromoCodeView and ClientCartApplyPromoCodeView */

export interface CartApplyPromoCodeResponse {
  message: string;
}

/** Matches ClientCartPromoCodesApplyAllView `POST` body */
export type ApplyAllPromoCodesMutation = string[];

/** Matches ClientCartPromoCodesApplyAllView `POST` response */
export type ApplyAllPromoCodesResponse = {
  applied_codes: string[];
};

/** Matches orders constants RefillTooSoonReason */
export enum RefillTooSoonReason {
  Travel = "TRAVEL",
  LostOrDamaged = "LOST_OR_DAMAGED",
  DifficultyDosing = "DIFFICULTY_DOSING",
}

export type CartDeliveryPickupUpdate = {
  is_pickup: boolean;
  cart_item_id: number;
};

export type CartDeliveryShippingUpdate = { shipping_method_id: number; cart_item_id: number };

/** Matches CartItemDeliveryGroupUpdateSerializer */
export type CartDeliveryUpdate = CartDeliveryPickupUpdate | CartDeliveryShippingUpdate;

/** matches controlled_substance ControlledSubstanceLevel */
export enum ControlledSubstanceLevel {
  FederallyControlled = "FEDERALLY_CONTROLLED",
  StateControlled = "STATE_CONTROLLED",
  NotControlled = "NOT_CONTROLLED",
}
